import CIcon from "@coreui/icons-react";
import { useEffect, useState } from "react";
import { CCardBody, CCol, CCollapse, CRow } from "@coreui/react";
import { Role } from "utils";

import ProfileImage from "../../ProfileImage";
import { getGenderString } from "../../../utils/strings";
import { getFormattedDate, getUtcFormattedDate } from "../../../utils/dates";
import IconButton from "../../Buttons/IconButton";
import StatusBadge from "../../Status/StatusBadge";
import CopyToClipboard from "../../CopyToClipboard";
import ThriverProfileLink from "../../ThriverProfileLink";
import ViewCareCirclesButton from "./ViewCareCirclesButton";
import CareHomePermissionButton from "./CareHomePermissionButton";
import EditUserButton from "./EditUserButton";
import FrequencyBar from "../../Frequency/FrequencyBar";
import UserSubscriptions from "./UserSubscriptions";
import CareHomeUserSubscriptionDetails from "./CareHomeUserSubscriptionDetails";
import DeleteUser from "./DeleteUser";
import { AppButton } from "components";
import { Link } from "react-router-dom";
import CustomSwitch from "components/CommonSwitch";
import { useEnableAndCreateAltumPersonMutation, useDisableAltumViewMutation, useResendInvitationMutation} from "pages/Users/Users.query";
import RiskScoreModal from "./RiskScoreModal";
import {
  useCalculateDailyRiskScoresMutation,
} from "pages/RiskScoring/RiskScoring.query";

const UserDetails = ({ show, user, tableType, resetSelectedUser, isType, setApiType, apiType }) => {
  const click = (e) => e.stopPropagation();
  const { mutate: enableAndCreateAltumPerson, isLoading: isEnablingAndCreating } =
    useEnableAndCreateAltumPersonMutation();
  const {mutate: resendInvitation,isLoading: isResendingInvitation,} = useResendInvitationMutation();

  const { mutate: disableAltumView, isLoading: isDisabling} = useDisableAltumViewMutation();
  const isSuperAdmin = Role.adminAccess() && !Role.admin_2_Access();

  const [altumEnabled, setAltumEnabled] = useState(false);
  const [showRiskScore, setShowRiskScore] = useState(false);

  useEffect(() => {
    setAltumEnabled(user?.enableAltumView || false);
  }, [user]);
const [riskResponse, setRiskResponse] = useState([]);

const {
  mutateAsync: calculateDailyRiskScores,
  isLoading: isRiskLoading,
  isError: isRiskError,
} = useCalculateDailyRiskScoresMutation();

const handleViewRiskScore = async () => {
  if (!user?.identifier || isRiskLoading) return;

  setShowRiskScore(true);
  setRiskResponse([]);

  try {
    const response = await calculateDailyRiskScores(user.identifier);
    setRiskResponse(response);
  } catch (error) {
    console.error("Risk score calculation failed:", error);
  }
};

  const urlObj = {
    "1": `/secure/dashboard/delete-account/${user?.identifier}`,
    "2": `/secure/dashboard/care-home/delete-account/${user?.identifier}`,
  }

 const handleAltumToggle = (checked) => {
  setAltumEnabled(checked);

  const mutation = checked
    ? enableAndCreateAltumPerson
    : disableAltumView;

  mutation(user.identifier, {
    onError: () => {
      setAltumEnabled(!checked);
    },
  });
};
const handleResendInvitation = () => {
  if (!user?.identifier || isResendingInvitation) return;

  resendInvitation(user.identifier);
};
  return (
    <CCollapse show={show} className="table-collapse" onClick={click}>
      <CCardBody style={{ position: "relative" }}>
        {/* Close button */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            padding: "15px",
            cursor: "pointer",
            zIndex: 200,
            borderRadius: "0 0 1rem 0",
          }}
          onClick={resetSelectedUser}
          className="hover-blue"
        >
          <CIcon name="cil-X" />
        </div>

        <CRow className="mb-3">
          {/* Image column */}
          <CCol
            xs="3"
            className="text-center d-flex align-items-center justify-content-center"
          >
            <div className="position-relative mx-4">
              <ProfileImage user={user} />
              <StatusBadge status={user?.status} />
            </div>
          </CCol>

          {/* Main info column */}
          <CCol xs="5">
            <div className="d-flex gap-10 flex-column">
              <span>
                <strong>Gender: </strong>
                <span className="pl-2">{getGenderString(user?.gender)}</span>
              </span>

              {user?.dob && (
                <span>
                  <strong>Birthday:</strong>
                  <span className="pl-2">{getUtcFormattedDate(user?.dob)}</span>
                  <span className="pl-2">({user?.age} years)</span>
                </span>
              )}

              {tableType !== "carehomes" ? (
                <div className="mt-1">
                  <strong>Mobile app use this week</strong>
                  <div className="mt-1">
                    <FrequencyBar days={user?.dateCheckList} />
                  </div>
                </div>
              ) : null}
            </div>
          </CCol>

          {/* Circles column */}
          <CCol xs="4">
            <div className="d-flex flex-column gap-10">
              <span>
                Member since:
                <strong className="pl-3">
                  {getFormattedDate(user?.registrationDate)}
                </strong>
              </span>
              {user.type === "thriver" && (
                <span>
                  Number of social circles:
                  <strong className="pl-3">
                    {user?.numbersOfSocialCircles || 0}
                  </strong>
                </span>
              )}
              <span>
                Number of care circles:
                <strong className="pl-3">{user?.numbersOfCircles || 0}</strong>
              </span>
              <div className="d-flex align-items-center">
                <ViewCareCirclesButton user={user} tableType={tableType} />
                {isSuperAdmin &&
                  user?.status === "pending" &&
                  user?.dynamicLink && (
                    <div className="ml-3">
                      <AppButton
                        name={isResendingInvitation ? "Sending..." : "Resend Invitation"}
                        className="deviceTypeButton"
                        size="md"
                        onClick={handleResendInvitation}
                      />
                    </div>
                  )}
                {user?.type === "thriver" && (
                  <div className="ml-3">
                    <AppButton
                      name={isRiskLoading ? "Calculating..." : "View Risk Score"}
                      className="py-1 px-2 deviceTypeButton"
                      size="md"
                      variant="outline"
                      onClick={handleViewRiskScore}
                      disabled={isRiskLoading}
                    />
                  </div>
                )}
              </div>
            </div>
          </CCol>
        </CRow>

        {tableType === "carehomes" && (
          <CareHomeUserSubscriptionDetails user={user} />
        )}

        {/* Dynamic link */}
        <CRow className="px-3">
          <CCol xs="12" className="m-0">
            {user?.status !== "active" && user.dynamicLink && (
              <CopyToClipboard
                textToCopy={user.dynamicLink}
                textToShow={`Dynamic Link: ${user.dynamicLink}`}
              />
            )}
          </CCol>
        </CRow>
      <div className="w-100 d-flex justify-content-center p-1 px-3 gap-15">
          <UserSubscriptions user={user} />

          <ThriverProfileLink user={user}>
            <IconButton icon="cil-chart-pie" name="View Profile" />
          </ThriverProfileLink>

          <ThriverProfileLink user={user} redirectToSpecificSection="health-summary">
          <IconButton icon="cil-chart-pie" name="View Health Summary" />
          </ThriverProfileLink>

          {(user.type === "caregiver") &&
            <Link
              title={`${user.type} profile`}
              to={{
                pathname: `/users-detail`,
                state: { userId: user?.identifier || user?.id, userType: user.type },
              }}
              style={{ width: "auto", display: "inline-block" }}
            >
              <AppButton
                name={'View Mood Analytics'}
                className={"py-1 px-2 deviceTypeButton"}
                size={"md"}
                variant={'outline'}
                onClick={() => { }}
              ></AppButton>
            </Link>}

          {isType === "B2B" && (
            <CareHomePermissionButton
              user={user}
              tableType={tableType}
            />
          )}


          <EditUserButton user={user} tableType={tableType} />
          {isSuperAdmin && user?.type === "thriver" && (
            <div
              className="deviceTypeButton d-flex align-items-center px-3"
              style={{
                width: "200px",
                height: "34px",
                borderRadius: "6px",
                gap: "10px",
              }}
            >
              <span
                style={{
                  flex: 1,
                  color: "#fff",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {altumEnabled ? "Disable AltumView" : "Enable AltumView"}
              </span>

              <label
                className="altum-switch"
                style={{
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={altumEnabled}
                  onChange={(e) => handleAltumToggle(e.target.checked)}
                  disabled={isEnablingAndCreating || isDisabling}
                />
                <span className="altum-slider"></span>
              </label>
            </div>
          )}
          {tableType === "SubAdmins" && (
            <Link
              to={{
                pathname: `/sub-admins/${user.identifier}/care-homes`,
                state: {
                  subAdminName: user.name,
                },
              }}
            >
              <AppButton
                name="Assign Care Homes"
                className="py-1 px-2 deviceTypeButton"
                size="md"
                variant="outline"
                onClick={() => { }}
              />
            </Link>
          )}

          {isType === "B2B" ?
            <DeleteUser
              title={user?.name}
              url={urlObj[apiType]}
              identifier={user?.identifier}
              isType={isType}
              setApiType={setApiType}
            />
            :
            (Role.adminAccess() ||
              (Role.careHomeAccess() && user?.type === "thriver")) && (
              <DeleteUser
                title={user?.name}
                url={`/secure/dashboard/delete-account/${user?.identifier}`}
                identifier={user?.identifier}
              />
            )}
        </div>
      </CCardBody>
      {showRiskScore && (
        <RiskScoreModal
          user={user}
          response={riskResponse}
          isLoading={isRiskLoading}
          isError={isRiskError}
          onClose={() => setShowRiskScore(false)}
        />
      )}
    </CCollapse>
  );
};

export default UserDetails;
